<template>
  <div class="goal-change-item">
    <div class="_label">
      <m-icon
        :type="icon"
        size="13"
        class="_icon"
      />
      <span class="_label-text">{{ label }}</span>
    </div>
    <div
      v-if="isDescription && formattingOnly"
      class="_hint"
    >
      {{ $t('goalChanges.formattingChanged') }}
    </div>
    <div
      v-else-if="isIcon"
      class="_values"
    >
      <template
        v-for="(value, index) in [change.previousValue, change.value]"
        :key="index"
      >
        <m-icon
          v-if="index > 0"
          type="right"
          size="11"
          class="_arrow"
        />
        <span
          v-if="isEmptyScalarValue(value)"
          class="_empty"
        >{{ $t('general.empty') }}</span>
        <m-icon-display
          v-else
          :icon="buildIcon(value)"
          :size="18"
        />
      </template>
    </div>
    <div
      v-else-if="isRelation"
      class="_relations"
    >
      <div
        v-for="relation in changes"
        :key="relation.uid"
        class="_values"
      >
        <item-title
          :title="relatedTitle(relation)"
          :icons="[{ value: buildIconFromEntity(relatedGoal(relation)) }]"
          :class="['_related-goal', { '-removed': relation.value === null }]"
        />
        <template v-if="shownWeights(relation).length > 0">
          <span class="_empty">{{ $t('goalChanges.weight') }}</span>
          <template
            v-for="(weight, index) in shownWeights(relation)"
            :key="index"
          >
            <m-icon
              v-if="index > 0"
              type="right"
              size="11"
              class="_arrow"
            />
            <span>{{ formatScalar(weight) }}</span>
          </template>
        </template>
      </div>
    </div>
    <div
      v-else-if="isGoalCycle"
      class="_values"
    >
      <template
        v-for="(cycles, index) in [change.previousGoalCycles, change.goalCycles]"
        :key="index"
      >
        <m-icon
          v-if="index > 0"
          type="right"
          size="11"
          class="_arrow"
        />
        <span
          v-if="cycles.length === 0"
          class="_empty"
        >{{ $t('general.empty') }}</span>
        <template v-else>
          <m-tag
            v-for="cycle in cycles"
            :key="cycle.uid"
            :title="cycle.title ?? $t('goalChanges.unavailableCycle')"
            :color="cycle.color"
            small
          />
        </template>
      </template>
    </div>
    <div
      v-else-if="isDescription || isTextDiff"
      class="_diff"
    >
      <span
        v-for="(part, index) in textDiff"
        :key="index"
        :class="['_part', `-${part.type}`]"
      >{{ part.text }}</span>
    </div>
    <div
      v-else
      class="_values"
    >
      <template v-if="isProperty">
        <goal-change-property-value
          :property="change.property"
          :property-value="change.previousPropertyValue"
        />
        <m-icon
          type="right"
          size="11"
          class="_arrow"
        />
        <goal-change-property-value
          :property="change.property"
          :property-value="change.propertyValue"
        />
      </template>
      <template v-else>
        <span :class="{ '_empty': isEmptyScalarValue(change.previousValue) }">{{ formatScalar(change.previousValue, change.previousMetric) }}</span>
        <m-icon
          type="right"
          size="11"
          class="_arrow"
        />
        <span :class="{ '_empty': isEmptyScalarValue(change.value) }">{{ formatScalar(change.value, change.metric) }}</span>
      </template>
    </div>
  </div>
</template>

<script setup>
import GoalChangePropertyValue from '@/components/goal/GoalChangePropertyValue.vue';
import ItemTitle from '@/components/ItemTitle.vue';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useGoals from '@/composables/goal/goals';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { buildIcon, buildIconFromEntity } from 'shared/lib/icon';
import { changeFieldLabel, formatScalarValue, isEmptyScalarValue, shownWeights } from '@/lib/goal-change/format';
import { computed } from 'vue';
import { diffPart, diffWords } from '@/lib/goal-change/diff';
import { documentText } from '@/lib/goal-change/document-text';
import { goalChangeField, propertyType } from 'shared/constants.json';
import { iconByType } from '@/lib/property';
import { relationFields } from '@/lib/goal-change/group';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  changes: { type: Array, required: true },
  goal: { type: Object, required: true },
});

const { t } = useI18n();
const { userLang } = useLoggedInUser();
const { goalSettings } = useGoalSettings();
const goalsSvc = useGoals();

const change = computed(() => props.changes[0]);

const isProperty = computed(() => change.value.field === goalChangeField.property && change.value.property !== null);
const isDescription = computed(() => change.value.field === goalChangeField.description);
const isTextDiff = computed(() => change.value.field === goalChangeField.title
  || (isProperty.value && change.value.property.type === propertyType.text));

const isRelation = computed(() => relationFields.includes(change.value.field));
const isGoalCycle = computed(() => change.value.field === goalChangeField.goalCycle);
const isIcon = computed(() => change.value.field === goalChangeField.icon);
const relatedGoal = (relation) => (relation.relatedGoal === null ? null : goalsSvc.selectSingle(relation.relatedGoal.uid) ?? null);
const relatedTitle = (relation) => relatedGoal(relation)?.title || t('goalChanges.unavailableGoal');

const formattingOnly = computed(() => textDiff.value.every((part) => part.type === diffPart.equal));

const textDiff = computed(() => {
  if (isDescription.value) {
    return diffWords(documentText(change.value.previousDescription), documentText(change.value.description));
  }
  if (isProperty.value) {
    return diffWords(change.value.previousPropertyValue?.text, change.value.propertyValue?.text);
  }
  return diffWords(change.value.previousValue, change.value.value);
});

const fieldIcons = {
  [goalChangeField.title]: 'align-left',
  [goalChangeField.description]: 'align-left',
  [goalChangeField.parent]: 'cluster',
  [goalChangeField.goalCycle]: 'retweet',
  [goalChangeField.icon]: 'smile',
  [goalChangeField.publishedAt]: 'global',
};

const icon = computed(() => {
  if (isProperty.value) {
    return iconByType({ type: change.value.property.type });
  }
  return fieldIcons[change.value.field] ?? 'line-chart';
});

const label = computed(() => changeFieldLabel(change.value, { t, language: userLang.value, goalName: goalSettings.value.featureNameSingular }));

const formatScalar = (value, metric = props.goal.metric) => formatScalarValue(change.value.field, value, { t, metric, language: userLang.value });
</script>

<style scoped lang="scss" type="text/scss">
  .goal-change-item {
    display: flex;
    flex-direction: column;
    gap: .4rem;
    padding: .6rem 0;

    ._label {
      display: flex;
      gap: .6rem;
      align-items: center;
      color: $font-color-secondary;
    }

    ._label-text {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &::first-letter {
        text-transform: uppercase;
      }
    }

    // Flex items don't shrink below their content by default, so goal titles and tags would overflow instead of
    // being cut, and long words in values would not wrap.
    ._values {
      display: flex;
      flex-wrap: wrap;
      gap: .6rem;
      align-items: center;
      overflow-wrap: anywhere;

      > *,
      :deep(.m-tag) {
        min-width: 0;
        max-width: 100%;
      }
    }

    ._relations {
      display: flex;
      flex-direction: column;
      gap: .4rem;
    }

    ._arrow,
    ._empty,
    ._hint {
      color: $font-color-secondary;
    }

    ._diff {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    ._related-goal.-removed {
      color: $font-color-secondary;
      text-decoration: line-through;
    }

    ._part {
      &.-removed {
        color: map_get($red, 'base');
        text-decoration: line-through;
      }

      &.-added {
        color: map_get($blue, 'base');
        background-color: map_get($blue, 'lighten-5');
      }
    }
  }
</style>
